import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import JobCard from './JobCard';
import JobFullView from './JobFullView';

interface Job {
  id: string;
  service_category: string;
  service_subcategory: string;
  city: string;
  postcode: string;
  address: string;
  description: string;
  is_instant: boolean;
  scheduled_date: string | null;
  created_at: string;
  images?: string[];
  customer_name?: string;
  customer_phone?: string;
}

interface AvailableJobsProps {
  providerId: string;
}

export default function AvailableJobs({ providerId }: AvailableJobsProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [hasOngoingJob, setHasOngoingJob] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<'default' | 'denied' | 'granted'>('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [providerProfile, setProviderProfile] = useState<any>(null);

  useEffect(() => {
    initializeProvider();
  }, [providerId]);

  const initializeProvider = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setProviderProfile(profile);
        
        // Request notification permission
        if ('Notification' in window) {
          const permission = await Notification.requestPermission();
          setNotificationPermission(permission);
        }

        loadAvailableJobs();
        checkOngoingJobs();
        subscribeToNewJobs(profile);
      }
    } catch (err) {
      console.error('Error initializing provider:', err);
    }
  };

  const subscribeToNewJobs = (profile: any) => {
    // Only subscribe if provider is fully active
    const isFullyActive = 
      profile.is_approved &&
      profile.is_active &&
      profile.subscription_status === 'active' &&
      profile.workwear_purchased &&
      profile.documents_uploaded &&
      profile.idv_status === 'verified';

    if (!isFullyActive) {
      return;
    }

    // Subscribe to job_broadcasts for this provider
    const subscription = supabase
      .channel(`provider-jobs-${profile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'job_broadcasts',
        filter: `provider_id=eq.${profile.id}`
      }, async (payload) => {
        // Fetch the full job details
        const { data: job } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', payload.new.job_id)
          .eq('status', 'pending')
          .single();

        if (job) {
          // Verify job matches provider's criteria
          const matchesCity = job.city === profile.city;
          const matchesCategory = profile.services?.includes(job.service_category);

          if (matchesCity && matchesCategory) {
            // Play notification sound
            playNotificationSound();
            
            // Show browser notification
            showBrowserNotification(job);
            
            // Send email notification
            sendEmailNotification(profile, job);
            
            // Reload jobs
            loadAvailableJobs();
          }
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const sendEmailNotification = async (profile: any, job: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-email-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            type: 'job_notification',
            to: profile.email,
            data: {
              providerName: profile.full_name || 'Provider',
              jobDetails: {
                service: job.service_subcategory,
                location: `${job.postcode}, ${job.city}`,
                date: job.is_instant ? 'ASAP' : job.scheduled_date,
                payment: 'TBD',
                description: job.description
              }
            }
          })
        }
      );
    } catch (err) {
      console.error('Error sending email notification:', err);
    }
  };

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      // Silently fail if audio context not available
    }
  };

  const showBrowserNotification = (job: any) => {
    if (notificationPermission === 'granted') {
      try {
        const notification = new Notification('New Job Available! 🔔', {
          body: `${job.service_subcategory} job in ${job.postcode}`,
          icon: '/favicon.png',
          tag: `job-${job.id}`,
          requireInteraction: true,
          vibrate: [200, 100, 200]
        });

        notification.onclick = () => {
          window.focus();
          handleViewDetails(job.id);
          notification.close();
        };
      } catch (err) {
        console.error('Error showing notification:', err);
      }
    }
  };

  const checkOngoingJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('id, status, is_instant, scheduled_date')
        .eq('provider_id', providerId)
        .in('status', ['accepted', 'in_progress']);

      if (error) throw error;

      const hasInstantJob = data?.some(job => {
        if (job.status === 'in_progress') return true;
        if (job.is_instant && job.status === 'accepted') return true;
        
        if (job.scheduled_date) {
          const scheduledTime = new Date(job.scheduled_date).getTime();
          const now = new Date().getTime();
          const hoursDiff = (scheduledTime - now) / (1000 * 60 * 60);
          if (hoursDiff < 4) return true;
        }
        
        return false;
      });

      setHasOngoingJob(hasInstantJob || false);
    } catch (err) {
      console.error('Error checking ongoing jobs:', err);
    }
  };

  const loadAvailableJobs = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('Session expired. Please sign in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-available-jobs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ providerId })
        }
      );

      if (!response.ok) {
        const result = await response.json();
        setError(result.message || 'Failed to load jobs');
        setLoading(false);
        return;
      }

      const result = await response.json();
      setJobs(result.jobs || []);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      
      // Mark job as viewed in broadcasts
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: provider } = await supabase
            .from('provider_profiles')
            .select('id')
            .eq('user_id', user.id)
            .single();

          if (provider) {
            await supabase
              .from('job_broadcasts')
              .update({ status: 'viewed', updated_at: new Date().toISOString() })
              .eq('job_id', jobId)
              .eq('provider_id', provider.id);
          }
        }
      } catch (err) {
        console.error('Error updating broadcast status:', err);
      }
    }
  };

  const handleAcceptJob = async (jobId: string) => {
    setAccepting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Session expired. Please sign in again.');
        setAccepting(false);
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/provider-login');
        }, 2000);
        return;
      }

      const { data: providerProfile } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!providerProfile) {
        setError('Provider profile not found.');
        setAccepting(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/accept-job`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            jobId,
            providerId: providerProfile.id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        
        if (errorData.error?.includes('already accepted')) {
          setError('This job has already been accepted by another provider.');
        } else if (errorData.error?.includes('cancelled')) {
          setError('This job has been cancelled by the client.');
        } else if (errorData.error?.includes('Complete onboarding')) {
          setError('You must complete all onboarding steps before accepting jobs.');
        } else {
          setError(errorData.error || 'Unable to accept job. Please try again.');
        }
        setAccepting(false);
        setSelectedJob(null);
        
        setTimeout(() => {
          loadAvailableJobs();
        }, 2000);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSuccess('Job accepted successfully! Redirecting to job details...');
        
        setJobs((prev) => prev.filter((job) => job.id !== jobId));
        setSelectedJob(null);
        
        setTimeout(() => {
          window.REACT_APP_NAVIGATE(`/job-tracking?id=${jobId}`);
        }, 1500);
      } else {
        setError('Job acceptance failed. Please try again.');
        setAccepting(false);
      }
    } catch (err) {
      console.error('Accept job error:', err);
      setError('Connection error. Please check your internet and try again.');
      setAccepting(false);
    }
  };

  const handleDeclineJob = async (jobId: string) => {
    setDeclining(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Session expired. Please sign in again.');
        setDeclining(false);
        return;
      }

      const { data: provider } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (provider) {
        await supabase
          .from('job_broadcasts')
          .update({ status: 'declined', updated_at: new Date().toISOString() })
          .eq('job_id', jobId)
          .eq('provider_id', provider.id);

        setJobs((prev) => prev.filter((job) => job.id !== jobId));
        setSelectedJob(null);
        setSuccess('Job declined successfully.');
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Decline job error:', err);
      setError('Failed to decline job. Please try again.');
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-[#0B3C5D] animate-spin"></i>
        <p className="mt-4 text-gray-600">Loading available jobs...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Available Jobs</h2>
        <div className="flex items-center gap-3">
          {notificationPermission !== 'granted' && (
            <button
              onClick={() => Notification.requestPermission().then(setNotificationPermission)}
              className="px-4 py-2 text-sm bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-notification-off-line"></i>
              Enable Notifications
            </button>
          )}
          <button
            onClick={loadAvailableJobs}
            className="px-4 py-2 text-[#0B3C5D] hover:bg-[#0B3C5D]/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {notificationPermission === 'granted' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <i className="ri-notification-3-line text-xl text-green-600"></i>
            <p className="text-sm text-green-800">
              🔔 Notifications enabled! You'll be alerted when new jobs become available.
            </p>
          </div>
        </div>
      )}

      {hasOngoingJob && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-3">
            <i className="ri-information-line text-xl text-yellow-600"></i>
            <p className="text-sm text-yellow-800">
              You have an ongoing job. You can only accept jobs scheduled 4+ hours from now.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="ri-checkbox-circle-line text-green-500 text-xl"></i>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <i className="ri-inbox-line text-5xl text-gray-400 mb-4"></i>
          <p className="text-gray-600">No available jobs in your area right now</p>
          <p className="text-sm text-gray-500 mt-2">Check back soon for new opportunities</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}

      {selectedJob && (
        <JobFullView
          job={selectedJob}
          onAccept={handleAcceptJob}
          onDecline={handleDeclineJob}
          onClose={() => setSelectedJob(null)}
          accepting={accepting}
          declining={declining}
        />
      )}
    </div>
  );
}
